import { pillarArticles, type PillarArticle } from '@/mocks/pillarArticles';
import { SITE_URL, SITE_NAME } from '@/lib/seo';

export const SITE_LOGO =
  'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/d367cf6745ee47f3fc82c76d3a54c50c.png';

const PUBLISHED = '2026-08-27';

function organizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: `${SITE_URL}/`,
    logo: {
      '@type': 'ImageObject',
      url: SITE_LOGO,
    },
  };
}

function websiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: `${SITE_URL}/`,
  };
}

function articleSchema(article: PillarArticle) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: PUBLISHED,
    dateModified: PUBLISHED,
    author: {
      '@type': 'Organization',
      name: SITE_NAME,
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: SITE_LOGO,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/${article.slug}`,
    },
  };
}

function breadcrumbSchema(
  items: { name: string; path: string }[],
): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${SITE_URL}${item.path}`,
    })),
  };
}

const faqData = [
  {
    q: 'O que é o CSSBuy Spreadsheet?',
    a: 'O CSSBuy Spreadsheet é um recurso organizado que ajuda na pesquisa de produtos, comparação de opções e consulta de guias sobre QC, preços, armazenamento e envio.',
  },
  {
    q: 'O CSSBuy é seguro?',
    a: 'A segurança depende de como você usa o serviço. Siga nossas recomendações no Safety Center, verifique URLs, proteja suas senhas e revise sempre o QC antes de confirmar envios.',
  },
  {
    q: 'Como funciona o shipping?',
    a: 'O custo de envio varia conforme peso, dimensões, destino e método escolhido. Consulte nosso guia completo para entender os fatores que influenciam o custo total.',
  },
  {
    q: 'Quais categorias estão disponíveis?',
    a: 'Shoes, Hoodies/Sweaters, T-Shirts, Jackets, Pants/Shorts, Headwear, Sets, Underwear, Jersey, Accessories, Bags, Belts, Perfumes e Other Stuff.',
  },
  {
    q: 'Como funciona o warehouse?',
    a: 'O warehouse permite que você armazene produtos comprados antes de enviá-los. Isso permite combinar vários produtos em um único envio, reduzindo custos de frete.',
  },
  {
    q: 'Posso devolver produtos?',
    a: 'As políticas de devolução dependem do vendedor e do agente. Verifique as políticas atuais antes de fazer uma compra. Sempre consulte o guia de safety para proteger suas transações.',
  },
  {
    q: 'Quais métodos de pagamento são aceitos?',
    a: 'Os métodos de pagamento variam. Verifique as opções disponíveis no site oficial. Sempre use métodos seguros e verifique URLs antes de realizar pagamentos.',
  },
];

function faqPageSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  };
}

function pageBreadcrumb(pathname: string): object | null {
  const crumbs: { name: string; path: string }[] = [
    { name: 'Início', path: '/' },
  ];

  const map: Record<string, string> = {
    '/cssbuy-spreadsheet': 'CSSBuy Spreadsheet',
    '/cssbuy-review': 'Review',
    '/cssbuy-shipping': 'Shipping',
    '/cssbuy-qc': 'QC',
    '/cssbuy-guide': 'Guia',
    '/cssbuy-safety': 'Segurança',
    '/cssbuy-faq': 'FAQ',
    '/categories': 'Categorias',
    '/cssbuy-spreadsheet-beginners': 'Guia para Iniciantes',
    '/about': 'Sobre',
    '/contact': 'Contato',
    '/disclaimer': 'Aviso Legal',
    '/privacy-policy': 'Política de Privacidade',
  };

  const path = pathname === '/' ? '/' : pathname.replace(/\/+$/, '');

  if (map[path]) {
    crumbs.push({ name: map[path], path });
    return breadcrumbSchema(crumbs);
  }

  const slug = path.replace(/^\//, '');
  const article = pillarArticles.find((a) => a.slug === slug);
  if (article) {
    crumbs.push({ name: 'CSSBuy Spreadsheet', path: '/cssbuy-spreadsheet' });
    crumbs.push({ name: article.title, path });
    return breadcrumbSchema(crumbs);
  }

  return null;
}

export function buildJsonLd(pathname: string): object[] {
  const path = pathname === '/' ? '/' : pathname.replace(/\/+$/, '');
  const schemas: object[] = [];

  const isHome = path === '/' || path === '/en' || path === '/de';

  if (isHome) {
    schemas.push(organizationSchema());
    schemas.push(websiteSchema());
    return schemas;
  }

  const slug = path.replace(/^\//, '');
  const article = pillarArticles.find((a) => a.slug === slug);

  if (article) {
    schemas.push(articleSchema(article));
  }

  if (path === '/cssbuy-faq') {
    schemas.push(faqPageSchema());
  }

  const crumbs = pageBreadcrumb(path);
  if (crumbs) {
    schemas.push(crumbs);
  }

  return schemas;
}